/**
 * pipeline/build-reference.ts
 *
 * Constructs canonical psref:/// URIs from Reference fields.
 * Inverse of parsePsrefUri in @pineseed/cli/src/reference-parser.
 *
 * Pure function — no I/O, no side effects, no fallbacks.
 */

import { ReferenceKind } from "../../../../lib/proto/gen/typescript/agency/reference/v1/reference";
import type { Reference } from "../../../../lib/proto/gen/typescript/agency/reference/v1/reference";

// Mirrors kindSlugMap in reference-parser.ts.
// Source of truth: agency/reference/v1/reference.proto.
const KIND_TO_SLUG: Partial<Record<ReferenceKind, string>> = {
  [ReferenceKind.PARTICIPANT]: "participant",
  [ReferenceKind.GROUP]: "group",
  [ReferenceKind.GENERAL]: "general",
  [ReferenceKind.URI]: "uri",
  [ReferenceKind.FILE]: "file",
  [ReferenceKind.MEDIA]: "media",
  [ReferenceKind.CHAT_MESSAGE]: "chat-message",
  [ReferenceKind.WORKSPACE]: "workspace",
  [ReferenceKind.THREAD]: "thread",
  [ReferenceKind.CONVERSATION]: "conversation",
};

/**
 * Build a canonical psref:/// URI from structured Reference fields.
 * Throws on UNSPECIFIED kind or missing id — fail-fast, no fallbacks.
 */
export function buildPsrefUri(
  ref: Pick<Reference, "kind" | "id" | "ext" | "version" | "tenant" | "params">,
): string {
  const slug = KIND_TO_SLUG[ref.kind];
  if (!slug) {
    throw new Error(
      `buildPsrefUri: cannot build URI for kind ${ReferenceKind[ref.kind] ?? ref.kind}`,
    );
  }
  if (!ref.id) {
    throw new Error("buildPsrefUri: id is required");
  }

  const url = new URL(`psref:///${slug}`);
  url.searchParams.set("id", ref.id);
  if (ref.ext) url.searchParams.set("ext", ref.ext);
  if (ref.version) url.searchParams.set("version", ref.version);
  if (ref.tenant) url.searchParams.set("tenant", ref.tenant);
  for (const [k, v] of Object.entries(ref.params ?? {})) {
    url.searchParams.set(k, v);
  }

  return url.toString();
}
